import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { api, formatApiErrorDetail } from "../lib/apiClient";
import { toast } from "sonner";

const EMPTY = { nombre: "", puesto: "", departamento: "", jefe_id: "", nivel: 2, avatar: "", email: "", telefono: "", estatus: "activo", orden: 0 };

export default function OrgMemberDialog({ open, onOpenChange, member, members, catalog, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(member);

  useEffect(() => {
    if (member) setForm({ ...EMPTY, ...member, jefe_id: member.jefe_id || "", avatar: member.avatar || "", email: member.email || "", telefono: member.telefono || "" });
    else setForm(EMPTY);
  }, [member, open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.nombre.trim()) { toast.error("El nombre es obligatorio"); return; }
    setSaving(true);
    const payload = { ...form, jefe_id: form.jefe_id || null, nivel: Number(form.nivel), orden: Number(form.orden) };
    try {
      if (isEdit) await api.put(`/org/members/${member.id}`, payload);
      else await api.post("/org/members", payload);
      toast.success(isEdit ? "Integrante actualizado" : "Integrante agregado");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const jefeOptions = members.filter((m) => !member || m.id !== member.id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-lg max-h-[90vh] overflow-y-auto" data-testid="org-member-dialog">
        <DialogHeader><DialogTitle>{isEdit ? "Editar integrante" : "Nuevo integrante"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Nombre completo *</Label>
            <Input value={form.nombre} onChange={(e) => set("nombre", e.target.value)} className="mt-1 rounded-sm" data-testid="org-nombre-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Puesto</Label>
              <Input value={form.puesto} list="org-puestos" onChange={(e) => set("puesto", e.target.value)} className="mt-1 rounded-sm" data-testid="org-puesto-input" />
              <datalist id="org-puestos">{(catalog.puestos || []).map((p) => <option key={p} value={p} />)}</datalist>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Departamento</Label>
              <Input value={form.departamento} list="org-deptos" onChange={(e) => set("departamento", e.target.value)} className="mt-1 rounded-sm" data-testid="org-depto-input" />
              <datalist id="org-deptos">{(catalog.departamentos || []).map((d) => <option key={d.name} value={d.name} />)}</datalist>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Jefe directo</Label>
              <Select value={form.jefe_id || "none"} onValueChange={(v) => set("jefe_id", v === "none" ? "" : v)}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="org-jefe-select"><SelectValue placeholder="Sin jefe" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin jefe (raíz)</SelectItem>
                  {jefeOptions.map((m) => <SelectItem key={m.id} value={m.id}>{m.nombre} — {m.puesto}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Nivel jerárquico</Label>
              <Input type="number" value={form.nivel} onChange={(e) => set("nivel", e.target.value)} className="mt-1 rounded-sm" data-testid="org-nivel-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Estatus</Label>
              <Select value={form.estatus} onValueChange={(v) => set("estatus", v)}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="org-estatus-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="activo">Activo</SelectItem>
                  <SelectItem value="inactivo">Inactivo</SelectItem>
                  <SelectItem value="vacante">Vacante</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Orden</Label>
              <Input type="number" value={form.orden} onChange={(e) => set("orden", e.target.value)} className="mt-1 rounded-sm" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Correo</Label>
              <Input value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Teléfono / Ext.</Label>
              <Input value={form.telefono} onChange={(e) => set("telefono", e.target.value)} className="mt-1 rounded-sm" />
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Avatar (URL)</Label>
            <Input value={form.avatar} onChange={(e) => set("avatar", e.target.value)} className="mt-1 rounded-sm" placeholder="https://..." />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button className="rounded-sm bg-slate-900 hover:bg-slate-800" onClick={save} disabled={saving} data-testid="org-save-btn">{saving ? "Guardando..." : "Guardar"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
